import _ from 'underscore';
import {v4 as uuid4} from 'uuid';

import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcessesBatchInfo} from 'notebook/structured-tables/ActionFunctionDAO';
import {LastSyncedUpstreamValue} from 'notebook/structured-tables/StructuredTableConstants';
import {TypedValue} from 'notebook/tables/TableConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import {StudyAssociation} from 'studies/StudyableDAO';
import * as net from 'util/Net';
import {withLoadingScreen} from 'util/Promises';
import * as TaskRunner from 'util/TaskRunner';

import {ProcessGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessGraphQL';
import {ProcessOutputGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessOutputGraphQL';
import {ProcessTaskGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessTaskGraphQL';
import {ProcessOutputSchemaGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessOutputSchemaGraphQL';
import {ProcessTaskSchemaGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {ProcessFlowchartSpec} from './ProcessSchemaDAO';

export function processDetailsURL(processId: string): string {
  return `/workflows/${processId}`;
}

export function processTaskDetailsURL(processTaskId: string): string {
  return `/workflow-tasks/${processTaskId}`;
}

function getApiUrlBase(itemType: string): string {
  if (itemType === ModelTypeConstants.PROCESS) {
    return '/1/api/processes';
  } else if (itemType === ModelTypeConstants.PROCESS_TASK) {
    return '/1/api/process-tasks';
  } else if (itemType === ModelTypeConstants.PROCESS_OUTPUT) {
    return '/1/api/process-outputs';
  } else {
    assert(false, 'Expected item to be one of Process, ProcessTask, or ProcessOutput');
  }
}

export interface BulkArchiveResponse {
  archivedItemIds: Array<string>;
}

export function bulkArchive(
  itemType: string,
  ids: Array<string>,
  purpose: string
): Promise<BulkArchiveResponse> {
  return TaskRunner.postJSONPromise({
    url: `${getApiUrlBase(itemType)}:bulk-archive`,
    data: {ids, purpose},
  });
}

export interface BulkUnarchiveResponse {
  unarchivedItemIds: Array<string>;
}

export function bulkUnarchive(itemType: string, ids: Array<string>): Promise<BulkUnarchiveResponse> {
  return TaskRunner.postJSONPromise({
    url: `${getApiUrlBase(itemType)}:bulk-unarchive`,
    data: {ids},
  });
}

export interface ExportToCSVResponse {
  url: string;
}

export function exportToCSV(itemType: string, ids: Array<string>): Promise<ExportToCSVResponse> {
  let idsKey: string;
  if (itemType === ModelTypeConstants.PROCESS) {
    idsKey = 'processIds';
  } else if (itemType === ModelTypeConstants.PROCESS_TASK) {
    idsKey = 'processTaskIds';
  } else if (itemType === ModelTypeConstants.PROCESS_OUTPUT) {
    idsKey = 'processOutputIds';
  } else {
    assert(false, 'Expected item to be one of Process, ProcessTask, or ProcessOutput');
  }

  return TaskRunner.postJSONPromise({
    url: `${getApiUrlBase(itemType)}:export-to-csv`,
    data: {[idsKey]: ids},
  });
}

export interface UpdateServerProcess {
  id: string;
  name?: string;
  watcherIds?: Array<string>;
  flowchartSpec?: ProcessFlowchartSpec;
  sourceType: string;
  actionOrigin: string;
  actionMethod: string;
  eventGroupId?: string;
}

export function updateProcess(data: UpdateServerProcess): Promise<ProcessGraphQL> {
  return withLoadingScreen(
    net.patchJSONPromise({
      url: `${getApiUrlBase(ModelTypeConstants.PROCESS)}/${data.id}`,
      // eslint-disable-next-line no-restricted-syntax
      data: _.omit(data, 'id'),
    }),
    'Saving task group...'
  );
}

export function bulkUpdateProcesses(processes: Array<UpdateServerProcess>): Promise<void> {
  return withLoadingScreen(
    net.postJSONPromise({
      url: `${getApiUrlBase(ModelTypeConstants.PROCESS)}:bulk-update`,
      data: {processes},
    }),
    `Saving task ${pluralize(processes.length, 'group')}...`
  );
}

interface TaskForServerSharedProperties {
  assigneeId?: string | null;
  scheduledOnDate?: string | null;
  sourceType: string;
  actionOrigin: ActionOrigin;
  actionMethod: ActionMethod;
  fields?: TagUpdateObject;
  studyIds?: Array<string>;
}

export type EditExistingTask = {
  id: string;
  statusId?: string;
  creationOriginId?: undefined;
  responsiblePartiesIds?: Array<string>;
} & TaskForServerSharedProperties;
type AddNewTaskToExistingProcess = {
  processId: string;
  creationOriginId?: string;
} & TaskForServerSharedProperties;
export type AddNewTaskToExistingNodeProcess = {
  nodeConfigId: string;
  fromItemId: string;
  schemaMapperId: string | null;
} & TaskForServerSharedProperties;

export type CreateServerProcessTask = AddNewTaskToExistingProcess | AddNewTaskToExistingNodeProcess;

export type CreateOrUpdateServerProcessTask = EditExistingTask | CreateServerProcessTask;

export interface BulkCreateOrUpdateProcessTasksResponse {
  tasks: Array<ProcessTaskGraphQL>;
  lastSyncedUpstreamValues: Array<Array<LastSyncedUpstreamValue>>;
}

export function bulkCreateOrUpdateProcessTasks({
  tasks,
  message,
  showLoadingScreen,
  totalTasksInTable,
  batchInfo,
  dataModsHeader,
}: {
  tasks: Array<CreateOrUpdateServerProcessTask>;
  message?: string;
  showLoadingScreen?: boolean;
  totalTasksInTable?: number;
  batchInfo?: ProcessesBatchInfo;
  dataModsHeader?: DataModificationsHeader;
}): Promise<BulkCreateOrUpdateProcessTasksResponse> {
  const promise = net.postJSONPromise({
    url: getApiUrlBase(ModelTypeConstants.PROCESS_TASK),
    data: {
      tasks,
      totalTasksInTable,
      batchInfo,
    },
    extraHeaders: dataModsHeader,
  });
  if (showLoadingScreen ?? true) {
    return withLoadingScreen(promise, message || `Saving ${pluralize(tasks.length, 'task')}...`);
  }
  return promise;
}

export function bulkCopyTasks(taskIds: Array<string>): Promise<{tasks: Array<ProcessTaskGraphQL>}> {
  const syncThreshold = BenchlingConfig.WORKFLOWS_MAX_TASKS_FOR_SYNCHRONOUS_BULK_TASK_COPY;

  if (syncThreshold === -1 || taskIds.length <= syncThreshold) {
    return withLoadingScreen(
      net.postJSONPromise({
        url: `${getApiUrlBase(ModelTypeConstants.PROCESS_TASK)}:copy`,
        data: {
          taskIds,
        },
      }),
      `Copying ${pluralize(taskIds.length, 'task')}...`
    );
  } else {
    return withLoadingScreen(
      TaskRunner.postJSONPromise({
        url: `${getApiUrlBase(ModelTypeConstants.PROCESS_TASK)}:copy-async`,
        data: {
          taskIds,
        },
      }),
      `Copying ${pluralize(taskIds.length, 'task')}...`
    );
  }
}

interface OutputForServerSharedProperties {
  fields?: TagUpdateObject;
  sourceType: string;
  actionOrigin: string;
  actionMethod: string;
}

export interface CreateServerProcessOutput extends OutputForServerSharedProperties {
  id?: undefined;
  processTaskId: string;
  creationOriginId: string;
  fromItemId?: string;
  schemaMapperId: string | null;
}

export interface UpdateServerProcessOutput extends OutputForServerSharedProperties {
  id: string;
  processTaskId?: undefined;
  creationOriginId?: undefined;
}

export type CreateOrUpdateServerProcessOutput = CreateServerProcessOutput | UpdateServerProcessOutput;

export function bulkCreateOrUpdateProcessOutputs({
  processOutputs,
  processTasks,
  showLoadingScreen,
  totalOutputsInTable,
  batchInfo,
  dataModsHeader,
}: {
  processOutputs: Array<CreateOrUpdateServerProcessOutput>;
  processTasks?: Array<EditExistingTask>;
  showLoadingScreen?: boolean;
  totalOutputsInTable?: number;
  batchInfo?: ProcessesBatchInfo;
  dataModsHeader?: DataModificationsHeader;
}): Promise<{processOutputs: Array<ProcessOutputGraphQL>; processTasks: Array<ProcessTaskGraphQL>}> {
  const promise = net.postJSONPromise({
    url: getApiUrlBase(ModelTypeConstants.PROCESS_OUTPUT),
    data: {processOutputs, processTasks, totalOutputsInTable, batchInfo},
    extraHeaders: dataModsHeader,
  });

  if (showLoadingScreen ?? true) {
    return withLoadingScreen(promise, `Saving ${pluralize(processOutputs.length, 'output')}...`);
  }
  return promise;
}

export interface CreateTaskForNewProcess extends TaskForServerSharedProperties {
  executionFlowchartClientId?: string;
}

export function createProcess(data: {
  schemaId: string;
  folderId: string;
  watcherIds: Array<string>;
  tasks: Array<TaskForServerSharedProperties>;
  sourceType: string;
  actionOrigin: string;
  actionMethod: string;
  creationModalId: string;
  rootProcessId?: string;
  flowcharts?: Array<ProcessFlowchartSpec>;
}): Promise<ProcessGraphQL> {
  const syncThreshold = BenchlingConfig.WORKFLOWS_MAX_TASKS_FOR_SYNCHRONOUS_TASKGROUP_CREATION;

  if (syncThreshold === -1 || data.tasks.length <= syncThreshold) {
    return withLoadingScreen(
      net.postJSONPromise({
        url: getApiUrlBase(ModelTypeConstants.PROCESS),
        data,
      }),
      'Saving task group...'
    );
  } else {
    return withLoadingScreen(
      TaskRunner.postJSONPromise({
        url: `${getApiUrlBase(ModelTypeConstants.PROCESS)}:create-async`,
        data,
      }),
      'Saving task group...'
    );
  }
}

export interface ExecuteIntoNewEntryParameters {
  entryName: string;
  folderId: string;
  initialDay: string;
  taskIds: Array<string>;
  tagSchemaId?: string;
  schemaTags?: TagUpdateObject;
  actionOrigin?: ActionOrigin;
  studyAssociations?: Array<StudyAssociation>;
}

export interface ExecuteIntoExistingEntryParameters {
  entryId: string;
  taskIds: Array<string>;
  tagSchemaId?: string;
  schemaTags?: TagUpdateObject;
  actionOrigin?: ActionOrigin;
}

export type ExecutionParameters = ExecuteIntoExistingEntryParameters | ExecuteIntoNewEntryParameters;

export function isExecuteIntoNewEntryParameters(
  executionParameters: ExecutionParameters
): executionParameters is ExecuteIntoNewEntryParameters {
  return (executionParameters as ExecuteIntoNewEntryParameters).entryName != null;
}

export function isExecuteIntoExistingEntryParameters(
  executionParameters: ExecutionParameters
): executionParameters is ExecuteIntoExistingEntryParameters {
  return (executionParameters as ExecuteIntoExistingEntryParameters).entryId != null;
}

export function executeProcessTasks(executionParameters: ExecutionParameters): Promise<{entryId: string}> {
  const executionAction = isExecuteIntoNewEntryParameters(executionParameters)
    ? 'execute'
    : 'execute-into-existing-entry';

  const syncThreshold = BenchlingConfig.WORKFLOWS_MAX_TASKS_FOR_SYNCHRONOUS_ENTRY_EXECUTION;

  if (syncThreshold === -1 || executionParameters.taskIds.length <= syncThreshold) {
    return withLoadingScreen(
      net.postJSONPromise({
        url: `${getApiUrlBase(ModelTypeConstants.PROCESS_TASK)}:${executionAction}`,
        data: executionParameters,
      }),
      `Preparing ${pluralize(executionParameters.taskIds.length, 'task')} for execution...`
    );
  } else {
    return withLoadingScreen(
      TaskRunner.postJSONPromise({
        url: `${getApiUrlBase(ModelTypeConstants.PROCESS_TASK)}:${executionAction}-async`,
        data: executionParameters,
      }),
      `Preparing ${pluralize(executionParameters.taskIds.length, 'task')} for execution...`
    );
  }
}

export interface OneTaskFinderResult {
  rowId: string;
  tasks?: Array<{id: string; humanId: string}>;
  errors?: Array<string>;
}

interface TaskFinderServerResponse {
  foundTasks: Array<OneTaskFinderResult>;
}

export type CreateDirectExecutionOutput = Pick<
  CreateServerProcessOutput,
  'processTaskId' | 'fields' | 'sourceType'
>;

export function runTaskFinder({
  processOutputSchemaId,
  availableTaskIds,
  rows,
}: {
  processOutputSchemaId: string;
  availableTaskIds: Array<string>;
  rows: Array<{rowId: string; typedValue: TypedValue}>;
}): Promise<TaskFinderServerResponse> {
  return net.postJSONPromise({
    url: `${getApiUrlBase(ModelTypeConstants.PROCESS_OUTPUT)}:find-tasks`,
    data: {processOutputSchemaId, availableTaskIds, rows},
  });
}

export function startDirectExecution(data: {
  taskIds: Array<string>;
  directExecutionUuid: string;
  actionOrigin?: ActionOrigin;
}): Promise<{tasks: Array<ProcessTaskGraphQL>}> {
  return net.postJSONPromise({
    url: `${getApiUrlBase(ModelTypeConstants.PROCESS_TASK)}:start-direct-execution`,
    data,
  });
}

export function undoDirectExecution(data: {
  taskIds: Array<string>;
  directExecutionUuid?: string;
}): Promise<{tasks: Array<ProcessTaskGraphQL>}> {
  return net.postJSONPromise({
    url: `${getApiUrlBase(ModelTypeConstants.PROCESS_TASK)}:undo-direct-execution`,
    data,
  });
}

export function completeDirectExecution(data: {
  tasks: Array<{
    id: string;
    sourceType: string;
    statusId: string;
  }>;
  outputs: Array<CreateDirectExecutionOutput>;
  directExecutionUuid: string;
}): Promise<{tasks: Array<ProcessTaskGraphQL>; outputs: Array<ProcessOutputGraphQL>}> {
  return withLoadingScreen(
    net.postJSONPromise({
      url: `${getApiUrlBase(ModelTypeConstants.PROCESS_TASK)}:complete-direct-execution`,
      data,
    }),
    `Creating ${pluralize(data.outputs.length, 'output')}...`
  );
}

export function completeDirectExecutionAsync(data: {
  tasks: Array<{
    id: string;
    sourceType: string;
    statusId: string;
  }>;
  outputs: Array<CreateDirectExecutionOutput>;
  directExecutionUuid: string;
}): Promise<{tasks: Array<ProcessTaskGraphQL>; outputs: Array<ProcessOutputGraphQL>}> {
  return withLoadingScreen(
    TaskRunner.postJSONPromise({
      url: `${getApiUrlBase(ModelTypeConstants.PROCESS_TASK)}:complete-direct-execution-async`,
      data,
    }),
    `Creating ${pluralize(data.outputs.length, 'output')}...`
  );
}

export function undoEntryExecution(data: {
  taskIds: Array<string>;
}): Promise<{tasks: Array<ProcessTaskGraphQL>}> {
  return net.postJSONPromise({
    url: `${getApiUrlBase(ModelTypeConstants.PROCESS_TASK)}:undo-entry-execution`,
    data,
  });
}

export interface GetAutomatedMappingResultFromTask {
  taskId: string;
  mapperId: string;
}

export interface GetAutomatedMappingResultFromOutput {
  outputId: string;
  mapperId: string;
}

export type GetAutomatedMappingResultItem =
  | GetAutomatedMappingResultFromOutput
  | GetAutomatedMappingResultFromTask;

export interface GetAutomatedMappingResultParams {
  automatedMappingInputs: Array<GetAutomatedMappingResultItem>;
}

export interface GetAutomatedMappingResultResponse {
  results: Array<{
    fields: Array<AutomatedMappingResultFieldAndValue>;
    toSchema: ProcessTaskSchemaGraphQL | ProcessOutputSchemaGraphQL;
    mapperId: string;
    fromItemId: string;
  }>;
}

export interface AutomatedMappingResultFieldAndValue {
  schemaField: SchemaField;
  value: TypedValue;
}

export function getAutomatedMappingResult(
  data: GetAutomatedMappingResultParams
): Promise<GetAutomatedMappingResultResponse> {
  return withLoadingScreen(
    net.postJSONPromise({
      url: `${getApiUrlBase(ModelTypeConstants.PROCESS_TASK)}:get-automated-mapping-result`,
      data,
    }),
    'Getting automated mapping result...'
  );
}

export interface RouteToEndNodesParams {
  endNodeIds: Array<string>;
  asyncJobRecordId: string;
}

export interface FixFailedRoutingAndMappingParams {
  tasks: Array<CreateServerProcessTask>;
  outputs: Array<CreateServerProcessOutput>;
  taskId: string;
  endNodes: Array<RouteToEndNodesParams>;
}

export interface FixFailedRoutingAndMappingResponse {
  createdTasks: Array<ProcessTaskGraphQL>;
  createdOutputs: Array<ProcessOutputGraphQL>;
  routedTask: ProcessTaskGraphQL;
}

export function fixFailedRoutingAndMapping(
  data: FixFailedRoutingAndMappingParams
): Promise<FixFailedRoutingAndMappingResponse> {
  return net.postJSONPromise({
    url: `/1/api/fix-failed-routing-and-mapping`,
    data,
  });
}
export function makeClientActionId(): string {
  return uuid4();
}
